'use client';

import { useState, useEffect } from 'react';

interface Section {
  id: string;
  label: string;
  offset: number;
}

interface SectionAnchorNavigationProps {
  sections: Section[];
  onSectionChange?: (sectionId: string) => void;
}

const SectionAnchorNavigation = ({
  sections,
  onSectionChange,
}: SectionAnchorNavigationProps) => {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id || '');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          onSectionChange?.(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, onSectionChange]);

  const scrollToSection = (sectionId: string, offset: number) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const targetPosition = element.offsetTop - offset;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <nav data-component-id="src/components/common/SectionAnchorNavigation.tsx:55:6" data-component-path="src/components/common/SectionAnchorNavigation.tsx" data-component-line="55" data-component-end-line="80" data-component-file="SectionAnchorNavigation.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20lg%3Afixed%20lg%3Aflex%20right-8%20top-1%2F2%20-translate-y-1%2F2%20z-section-nav%20flex-col%20space-y-4%22%7D" className="hidden lg:fixed lg:flex right-8 top-1/2 -translate-y-1/2 z-section-nav flex-col space-y-4">
        {sections.map((section) => (
          <button data-component-id="src/components/common/SectionAnchorNavigation.tsx:57:10" data-component-path="src/components/common/SectionAnchorNavigation.tsx" data-component-line="57" data-component-end-line="78" data-component-file="SectionAnchorNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22group%20relative%20flex%20items-center%22%7D"
            key={section.id}
            onClick={() => scrollToSection(section.id, section.offset)}
            className="group relative flex items-center"
            aria-label={`Navigate to ${section.label}`}
          >
            <div data-component-id="src/components/common/SectionAnchorNavigation.tsx:63:12" data-component-path="src/components/common/SectionAnchorNavigation.tsx" data-component-line="63" data-component-end-line="68" data-component-file="SectionAnchorNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                activeSection === section.id
                  ? 'bg-primary w-3 h-3' :'bg-border group-hover:bg-text-secondary'
              }`}
            />
            <span data-component-id="src/components/common/SectionAnchorNavigation.tsx:69:12" data-component-path="src/components/common/SectionAnchorNavigation.tsx" data-component-line="69" data-component-end-line="77" data-component-file="SectionAnchorNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
              className={`absolute right-6 whitespace-nowrap px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeSection === section.id
                  ? 'opacity-100 translate-x-0 bg-primary text-primary-foreground'
                  : 'opacity-0 translate-x-2 bg-surface text-text-secondary group-hover:opacity-100 group-hover:translate-x-0'
              }`}
            >
              {section.label}
            </span>
          </button>
        ))}
      </nav>

      <nav data-component-id="src/components/common/SectionAnchorNavigation.tsx:82:6" data-component-path="src/components/common/SectionAnchorNavigation.tsx" data-component-line="82" data-component-end-line="96" data-component-file="SectionAnchorNavigation.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22lg%3Ahidden%20fixed%20bottom-24%20left-1%2F2%20-translate-x-1%2F2%20z-section-nav%22%7D" className="lg:hidden fixed bottom-24 left-1/2 -translate-x-1/2 z-section-nav">
        <div data-component-id="src/components/common/SectionAnchorNavigation.tsx:83:8" data-component-path="src/components/common/SectionAnchorNavigation.tsx" data-component-line="83" data-component-end-line="95" data-component-file="SectionAnchorNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%20px-4%20py-2%20rounded-full%20bg-surface%2F95%20backdrop-blur-sm%20border%20border-border%20shadow-card%22%7D" className="flex items-center space-x-3 px-4 py-2 rounded-full bg-surface/95 backdrop-blur-sm border border-border shadow-card">
          {sections.map((section) => (
            <button data-component-id="src/components/common/SectionAnchorNavigation.tsx:85:12" data-component-path="src/components/common/SectionAnchorNavigation.tsx" data-component-line="85" data-component-end-line="93" data-component-file="SectionAnchorNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={section.id}
              onClick={() => scrollToSection(section.id, section.offset)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                activeSection === section.id
                  ? 'bg-primary w-8 h-2' :'bg-border'
              }`}
              aria-label={`Navigate to ${section.label}`}
            />
          ))}
        </div>
      </nav>
    </>
  );
};

export default SectionAnchorNavigation;